import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";

import { Stack } from "@mui/system";
import { KeyboardArrowDown } from "@mui/icons-material";

import store from "../../mobx";
import { setPurchaseDialog } from "../../components/Dialog/setPurchaseDialog";
import { ReactTyped } from "react-typed";
import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";

export const ValueProp = observer(function ValueProp() {
  const { isPhone } = store.mediaBreakpointStore;

  return (
    <Stack
      height="100%"
      sx={{
        ...(isPhone
          ? {
              pt: 12,
            }
          : { justifyContent: "center" }),

        position: "relative",
      }}
    >
      <Stack direction="row" justifyContent="space-between">
        <Stack
          spacing={isPhone ? 3 : 4}
          justifyContent={isPhone ? undefined : "center"}
          alignItems="center"
          sx={{
            px: 2,

            height: "100%",
            width: isPhone ? undefined : "50vw",
            position: isPhone ? "absolute" : "relative",
          }}
        >
          <Stack alignItems="center">
            <Typography
              variant="h2"
              textAlign="center"
              sx={(theme) => ({
                fontFamily: "PermanentMarker",
                fontSize: isPhone ? 44 : 56,
                mb: isPhone ? undefined : -0.5,
                lineHeight: "36px",
                WebkitTextStroke: 2,
                WebkitTextStrokeColor: theme.palette.altPrimary.main,
                color: "black",
              })}
            >
              Mix.Quick
            </Typography>
          </Stack>
          <Stack justifyContent="center" alignItems="center" spacing={4}>
            <Box>
              <Typography
                paragraph
                fontSize={isPhone ? 32 : 48}
                textAlign="center"
                variant="h4"
                fontFamily="Roboto"
                // color="#ccc"
              >
                <ReactTyped
                  strings={[
                    "Learn faster.",
                    "Sound better.",
                    "Mix like a pro.",
                  ]}
                  typeSpeed={50}
                  backSpeed={30}
                  loop
                />
              </Typography>
              <Typography textAlign="center" paragraph>
                Speed up your mixing workflow by <b>4x</b> with superior
                results, novice or pro
              </Typography>
            </Box>
            {isPhone && <PreviewImageMobile />}
            {isPhone ? <MobileCTA /> : <DesktopCTA />}
            {/* <Box /> */}
          </Stack>
        </Stack>
        {!isPhone && <PreviewImage />}
      </Stack>
    </Stack>
  );
});

const images = [
  "/screenshots/clipper.jpg",
  "/screenshots/saturator.jpg",
  "/screenshots/stereo-widener.jpg",
];

const MobileCTA = observer(function MobileCTA() {
  const navigate = useNavigate();
  const isPurchased =
    store.userStore.profile.products?.MixQuickEarlyAccess?.purchased === true;
  return (
    <>
      <Stack spacing={2} direction="row" alignItems="center">
        <Button
          variant="contained"
          color="secondary"
          sx={{
            bgcolor: "#aaa",
            // ml: "auto",
            // border: "1px solid",
            fontFamily: "Roboto",
          }}
          onClick={async () => {
            if (store.userStore.session.isAnonymous) {
              await store.userStore.authWithGoogle();
            }

            window.open(
              "https://storage.googleapis.com/last-year-18294.firebasestorage.app/Mix.Quick.vst3.zip"
            );
          }}
        >
          Download For free
        </Button>
        <Button
          variant="contained"
          color="altPrimary"
          sx={{
            // ml: "auto",
            color: "black",
            // border: "1px solid",
            fontFamily: "Roboto",
          }}
          onClick={() => {
            if (isPurchased) {
              navigate("/products");
              return;
            }

            setPurchaseDialog();
          }}
        >
          Purchase ($20)*
        </Button>
      </Stack>
      <Typography>*Purchase includes free updates for life</Typography>
    </>
  );
});

const DesktopCTA = observer(function DesktopCTA() {
  const navigate = useNavigate();
  const isPurchased =
    store.userStore.profile.products?.MixQuickEarlyAccess?.purchased === true;
  return (
    <Stack spacing={2} direction="row" alignItems="center">
      <Card
        sx={{
          //
          minWidth: 250,
          bgcolor: "transparent",
        }}
      >
        <CardHeader title="Download" subheader="Free (Early Access)" />
        <CardContent>
          <Stack>
            <Button
              variant="contained"
              color="secondary"
              sx={{
                bgcolor: "#aaa",
                // ml: "auto",
                // border: "1px solid",
                fontFamily: "Roboto",
              }}
              onClick={async () => {
                if (store.userStore.session.isAnonymous) {
                  await store.userStore.authWithGoogle();
                }

                window.open(
                  "https://storage.googleapis.com/last-year-18294.firebasestorage.app/Mix.Quick.vst3.zip"
                );
              }}
            >
              Download
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card
        sx={{
          //
          minWidth: 250,
          // bgcolor: "transparent",
        }}
      >
        <CardHeader title="Purchase" subheader="Unlimited upgrades for life" />
        <CardContent>
          <Stack>
            <Button
              variant="contained"
              color="altPrimary"
              sx={{
                // ml: "auto",
                color: "black",
                // border: "1px solid",
                fontFamily: "Roboto",
              }}
              onClick={() => {
                if (isPurchased) {
                  navigate("/products");
                  return;
                }

                setPurchaseDialog();
              }}
            >
              Purchase ($20)
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
});

export default function PreviewImage() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollTo = useCallback(
    (index) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  );

  useEffect(() => {
    const autoScroll = setInterval(() => {
      if (!emblaApi) return;
      const nextIndex = (selectedIndex + 1) % images.length;
      scrollTo(nextIndex);
    }, 3000);

    return () => clearInterval(autoScroll);
  }, [selectedIndex, scrollTo, emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi]);

  return (
    <Stack sx={{ width: "50vw", overflow: "hidden", pr: 8 }}>
      <Box ref={emblaRef} sx={{ overflow: "hidden", borderRadius: 4 }}>
        <Box sx={{ display: "flex" }}>
          {images.map((src, index) => (
            <Box
              key={index}
              sx={{
                flex: "0 0 100%",
                paddingRight: 2,
              }}
            >
              <Box
                component="img"
                src={src}
                alt={`Screenshot ${index + 1}`}
                sx={{
                  width: "100%",
                  borderRadius: 4,
                  border: "2px solid #888",
                  transition: "all 0.6s ease-in-out",
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Stack>
  );
}

function PreviewImageMobile() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollTo = useCallback(
    (index) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  );

  useEffect(() => {
    const autoScroll = setInterval(() => {
      if (!emblaApi) return;
      const nextIndex = (selectedIndex + 1) % images.length;
      scrollTo(nextIndex);
    }, 3000);

    return () => clearInterval(autoScroll);
  }, [selectedIndex, scrollTo, emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi]);

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{ width: "100%", overflow: "hidden" }}
    >
      <Box ref={emblaRef} sx={{ overflow: "hidden", borderRadius: 4 }}>
        <Box sx={{ display: "flex" }}>
          {images.map((src, index) => (
            <Box
              key={index}
              sx={{
                flex: "0 0 100%",
              }}
            >
              <Box
                component="img"
                src={src}
                alt={`Screenshot ${index + 1}`}
                sx={{
                  width: "100%",
                  borderRadius: 4,
                  border: "2px solid #888",
                  transition: "all 0.6s ease-in-out",
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Stack>
  );
}
